import moment from 'moment'
import { avatarColorPresets, avatarColors } from '@/utils/constants/ui-constants'
import { DeleteTypeExpression, ListFilterTypeExpression } from '@/enum/LinqExpressionType'

export const generateRandomHexColorString = (): string => {
  const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16)}`
  if (randomColor.length !== 7) {
    return generateRandomHexColorString()
  }
  return randomColor
}

export const generateRandomFilename = () => {
  const userId = localStorage.getItem('userId')
  const currentTimestamp = moment(new Date()).format('MM_DD_YYYY_h_mm_ss')
  const randomNumber = Math.floor(Math.random() * 1000000)
  return `${userId}_${currentTimestamp}_${randomNumber}`
}

export const generateAvatarColor = (initials: string) => {
  const initialLowered = initials?.[0]?.toLowerCase()
  const presetColor = avatarColorPresets.find((p: any) => p.group.includes(initialLowered))
  const selectedColor = presetColor
    ? presetColor.color
    : avatarColors[Math.floor(Math.random() * avatarColors.length)]

  return selectedColor
}

export const generateUserNameInitials = (name: string) => {
  const nameParts = name.split(' ')
  let initials = ''
  if (nameParts.length > 1) {
    initials = `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
  } else {
    initials = `${nameParts[0][0]}${nameParts[0][1]}`
  }
  return initials
}

export const getListByIdExpression = (
  listItemKey: string,
  filterKey: string,
  values: number | boolean | string | number[] | string[],
  isNotInList?: boolean
) => {
  let expression = ''
  const listExpression: Array<string> = []
  if (Array.isArray(values)) {
    values.forEach((value) => {
      const val = typeof value === 'boolean' ? value : `Guid("${value}")`
      listExpression.push(val)
    })
    return `${listItemKey}.Any(${filterKey} in (${listExpression})) ${isNotInList ? `!= true` : ''}`
  }
  expression = values as string
  return `${listItemKey}.Any(${filterKey} == Guid("${expression}")) ${isNotInList ? `!= true` : ''}`
}

export const generateStringFromObjectFilters = (
  obj: Record<string, any>,
  shouldNotPrependParentKey?: boolean
) => {
  let resultString = ''

  Object.keys(obj).forEach((key) => {
    const value = obj[key]

    if (typeof value === 'object') {
      Object.keys(value).forEach((subKey) => {
        const subValue = value[subKey]

        if (Array.isArray(subValue)) {
          subValue.forEach((item) => {
            if (shouldNotPrependParentKey) {
              resultString += `${subKey}=${item}&`
            } else {
              resultString += `${key}.${subKey}=${item}&`
            }
          })
        } else if (shouldNotPrependParentKey) {
          resultString += `${subKey}=${subValue}&`
        } else {
          resultString += `${key}.${subKey}=${subValue}&`
        }
      })
    } else if (!shouldNotPrependParentKey) {
      resultString += `${key}=${value}&`
    }
  })

  resultString = resultString.slice(0, -1)
  return resultString.replace(/\.FilterExpression\./g, '.')
}

export const generateAssignmentFilterExpression = (filters: Record<string, any[]>): string => {
  return Object.entries(filters)
    .map(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        return values.map((value) => `${key}="${value}"`).join("||");
      }
      return null;
    })
    .filter(Boolean)
    .join(" AND "); 
}

export const generateDeleteFiltersExpressions = (
  filters: Record<
    string,
    | boolean
    | number
    | string
    | (number | string)[]
    | Record<string, boolean | string | number | (string | number)[]>
  >
) => {
  let expression = {}
  Object.keys(filters).forEach((key) => {
    if (key === ListFilterTypeExpression.Filter) {
      expression = {
        ...expression,
        [DeleteTypeExpression.Expression]: filters[key]
      }
    }
    if (key === ListFilterTypeExpression.SearchText) {
      expression = {
        ...expression,
        [DeleteTypeExpression.SearchText]: filters[key]
      }
    }
  })
  return expression
}

export const generateFlatListRows = (
  flatList: Array<any>,
  propertiesToCheck: Array<string>,
  escapeCharacter: string
) => {
  const result = flatList.map((row: any, index: any) => {
    const isSameIdAsPrevious = index > 0 && row.id === flatList[index - 1].id

    const modifiedRow = { ...row }

    propertiesToCheck.forEach((property) => {
      modifiedRow[property] =
        isSameIdAsPrevious && row[property] === flatList[index - 1][property]
          ? escapeCharacter
          : row[property]
    })

    return modifiedRow
  })

  return result
}

export const calculatePercentage = (value: number, percentageValue: number): number => {
  if (value && percentageValue) {
    return parseFloat(((percentageValue * 100) / value).toFixed(1))
  }
  return 0
}

export const generateMockData = (options: {
  format: Record<string, string>
  totalRecords: number
}) => {
  const { format, totalRecords } = options
  const mockData = []
  for (let i = 0; i < totalRecords; i++) {
    const mockRow = Object.keys(format).reduce((acc: any, key) => {
      if (format[key] === 'string') {
        acc[key] = `Mock ${key} ${i}`
      } else if (format[key] === 'number') {
        const randomNumber = Math.random()
        acc[key] = randomNumber < 0.2 ? 0 : Math.floor(Math.random() * 100) + 1
      } else if (format[key] === 'date') {
        acc[key] = new Date().toISOString()
      }
      return acc
    }, {})
    mockData.push(mockRow)
  }
  return mockData
}

export const generateRandomNumber = (min?: number, max?: number) => {
  if (min !== undefined && max) {
    return Math.floor(Math.random() * (max - min + 1)) + min
  }
  return Math.random()
}

export const generateBase64FromFile = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    if (typeof file === 'string') {
      resolve(file)
    }
    const fileReader = new FileReader()
    fileReader.readAsDataURL(file)
    fileReader.onload = (fileReaderEvent) => {
      if (fileReaderEvent.target?.result) {
        resolve(fileReaderEvent.target.result as string)
      } else {
        reject('Could not find file target')
      }
    }
    fileReader.onerror = () => {
      reject('Encountered error when generating base64 file format')
    }
  })
}

export const generateIterableListFromEnum = (enumValue: Object, enumLabel?: any) => {
  return Object.entries(enumValue)
    .filter(([key, value]) => (typeof value === 'number' ? true : !Number.isNaN(Number(value))))
    .map(([key, value]) => ({ value, title: enumLabel ? enumLabel[key] : key }))
}
